import { createFileRoute, notFound } from "@tanstack/react-router";
import { getDoctor, getDoctors } from "@/lib/server-functions";
import { slugify } from "@/lib/slugs";
import DoctorDetail from "@/pages/DoctorDetail";
import ogImage from "@/assets/knmc-og.jpeg";
import { getPageUrl } from "@/lib/site";
import { doctorJsonLd } from "@/lib/jsonld";

export function buildDoctorSlugMap(
  doctors: { name: string; practitioner_name: string; department: string }[],
): Map<string, string> {
  const slugCount = new Map<string, number>();
  const map = new Map<string, string>();

  for (const d of doctors) {
    const deptSlug = slugify(d.department);
    const nameSlug = slugify(d.practitioner_name);
    const raw = `${deptSlug}/${nameSlug}`;
    const count = slugCount.get(raw) ?? 0;
    slugCount.set(raw, count + 1);
    const suffix = count > 0 ? `-${count + 1}` : "";
    map.set(`${raw}${suffix}`, d.name);
  }

  return map;
}

export const Route = createFileRoute(
  "/doctors/$departmentName/$practitionerName"
)({
  loader: async ({ params }) => {
    try {
      const doctors = await getDoctors();
      const map = buildDoctorSlugMap(doctors);
      const key = `${params.departmentName}/${params.practitionerName}`;
      const doctorName = map.get(key);

      if (!doctorName) throw notFound();

      const doctor = await getDoctor({ data: doctorName });
      return { doctor };
    } catch {
      throw notFound();
    }
  },

  head: ({ params, loaderData }) => {
    const doctor = loaderData?.doctor;
    const name = doctor?.practitioner_name || params.practitionerName;
    const dept = doctor?.department || params.departmentName;

    const pagePath = `/doctors/${encodeURIComponent(params.departmentName)}/${encodeURIComponent(params.practitionerName)}`;
    const pageUrl = getPageUrl(pagePath);

    return {
      meta: [
        {
          title: doctor
            ? `${doctor.practitioner_name} — KNMC Kathmandu`
            : `${name} — Doctor in Kathmandu | KNMC`,
        },
        {
          name: "description",
          content: doctor
            ? `Book an appointment with ${doctor.practitioner_name}, ${doctor.department} specialist at KNMC Hospital in Kathmandu, Nepal.`
            : `Book an appointment with Dr. ${name} at KNMC Hospital in Kathmandu, Nepal.`,
        },
        {
          name: "keywords",
          content: doctor
            ? `${doctor.practitioner_name}, ${doctor.department}, ${doctor.specialties?.join(", ") || ""}, doctor in Kathmandu, KNMC`
            : `doctor in Kathmandu, KNMC specialists`,
        },
        {
          property: "og:title",
          content: doctor
            ? `${doctor.practitioner_name} — ${doctor.department} Specialist | KNMC Kathmandu`
            : `${name} — KNMC Kathmandu`,
        },
        {
          property: "og:description",
          content: doctor
            ? `Book an appointment with ${doctor.practitioner_name}, ${doctor.department} specialist at KNMC Hospital in Kathmandu.`
            : `Book an appointment with Dr. ${name} at KNMC Hospital in Kathmandu.`,
        },
        {
          property: "og:image",
          content: getPageUrl(ogImage),
        },
        {
          property: "og:url",
          content: pageUrl,
        },
        ...(doctor ? [doctorJsonLd(doctor, pagePath)] : []),
      ],

      links: [
        {
          rel: "canonical",
          href: pageUrl,
        },
      ],
    };
  },

  component: () => {
    const { doctor } = Route.useLoaderData();
    return <DoctorDetail doctor={doctor} />;
  },
});